/**
 * ground-truth-doc.ts — the GroundTruthDoc export schema + coordinate
 * reconciliation for editor-authored logical-structure ground truth.
 *
 * Epic: docs/epics/active/editor-ground-truth-authoring-260813/main.md Phase 2
 * (Ph1b RATIFY-WITH-REVISES). Wire-contract SSOT:
 * system-models/wire-contracts/ground-truth-doc.schema.json.
 *
 * WHY this module (the load-bearing artifact). A human hand-tags a document in the
 * editor to assert its correct structure; `exportGroundTruth` serializes that IR
 * into a durable, versioned `GroundTruthDoc` — the single source of truth the
 * extraction scorer + the structure/a11y-receipt/selector benchmark oracles consume
 * (A.9; NOT the nav-cost oracle — Ph1b R3). The element basis is the a11y-view
 * `ViewItemBox` field-set (bbox:BboxNorm 0..1 + readingOrderIndex + role + alt +
 * decorative + geometryConfidence), NOT `SerializedStructNode` — the serializer
 * drops geometry, and geometry is exactly what G-6 bbox-IoU scoring needs (Ph1b
 * R1/R4).
 *
 * The FOUR-bbox-space hazard (Ph1b R2, silent-wrong-for-months). Geometry lives in
 * different spaces:
 *   1. IR Block.bbox    — BboxXywh {x,y,w,h} in PDF user-space, BOTTOM-LEFT origin
 *                         (same space as EditorIRBBox; `set-bbox`/`create-element`
 *                         store `canvasBboxToPdf` output, and the wire IR arrives in
 *                         PDF user-space, never transformed on load). `y` is the box's
 *                         BOTTOM edge from the page bottom.
 *   2. a11y-view bbox   — BboxNorm 0..1 vs the page canvas, TOP-LEFT origin.
 *   3. extraction nodes — PDF user-space [x0 y0 x1 y1] CORNER form, BOTTOM-LEFT origin.
 *   (`canvasBboxToNorm` remains available for a genuine TOP-LEFT canvas-pixel box —
 *   not the shape Block.bbox actually carries.)
 * The canonical SCORING space is BboxNorm 0..1 (top-left origin). `pdfUserSpaceXywhToNorm`
 * routes the hand-authored side (1) WITH the origin flip; `pdfUserSpaceCornerToNorm`
 * routes the extraction side (3), also with the flip. A wrong/dropped origin flip yields
 * IoU≈0; the R2 pin (`ground-truth-doc.jsdom.test.ts`) asserts IoU≈1.0 for the SAME
 * region after reconciliation — over a REAL PDF-space xywh box, not a synthetic top-left
 * one (the earlier blind spot).
 *
 * No DOM globals at module scope. Editor imports are type-only (erased at
 * runtime) EXCEPT `./log.js`, whose module scope touches no DOM (its `window`
 * access is lazy + try/guarded; `log.warn` is a bare `console.warn`), so this
 * module stays Node-importable without a compiled editor tree.
 *
 * @module editor/ground-truth-doc
 */
import { log } from "./log.js";
// ─── The exported schema shape (mirrors ground-truth-doc.schema.json) ──────────
/** Current GroundTruthDoc schema version (semver). */
export const GROUND_TRUTH_SCHEMA_VERSION = "1.0.0";
/**
 * Thrown when the IR cannot be exported as sound ground truth (fail-loud, A.14).
 *
 * When the cause is geometry-less blocks (GT-INV-1b), `unplacedBlocks` carries
 * the full set the author must place — the flow layer / a future editor overlay
 * can consume it to highlight exactly which blocks need a box. Empty for the
 * other fail-loud causes (bad page dims, non-positive canvas).
 */
export class GroundTruthExportError extends Error {
    unplacedBlocks;
    constructor(message, unplacedBlocks = []) {
        super(message);
        this.name = "GroundTruthExportError";
        this.unplacedBlocks = unplacedBlocks;
    }
}
// ─── Coordinate reconciliation (Ph1b R2 — the four-bbox-space contract) ────────
/**
 * The canonical scoring space is BboxNorm 0..1 with a TOP-LEFT origin. Both the
 * hand-authored side and the extraction side reconcile INTO it before any IoU.
 */
/**
 * Generic TOP-LEFT canvas-pixel adapter: for a `{x,y,w,h}` box already in a
 * top-left-origin space (the same convention as BboxNorm), normalization is a pure
 * per-axis divide by the canvas dimensions — NO origin flip. NB: the IR `Block.bbox`
 * is NOT this shape (it is PDF user-space, bottom-left — see `pdfUserSpaceXywhToNorm`,
 * which routes the hand-authored export leg); this helper is retained for genuinely
 * top-left canvas boxes and as the R2 "no-flip is wrong" discriminator.
 */
export function canvasBboxToNorm(bbox, canvasW, canvasH) {
    if (!(canvasW > 0) || !(canvasH > 0)) {
        throw new GroundTruthExportError(`canvasBboxToNorm: non-positive canvas dims (${canvasW}x${canvasH})`);
    }
    return { x: bbox.x / canvasW, y: bbox.y / canvasH, w: bbox.w / canvasW, h: bbox.h / canvasH };
}
/**
 * Extraction side: `pdf-struct-tree-extract` nodes carry `[x0 y0 x1 y1]` CORNER form
 * in PDF user-space with a BOTTOM-LEFT origin. Reconciling to the canonical
 * top-left-origin BboxNorm requires (a) corners→width/height and (b) the ORIGIN FLIP
 * on y: the box's top edge in top-left space is `pageHeight - y1`. Getting this flip
 * wrong is the silent-noise failure the R2 pin guards against.
 */
export function pdfUserSpaceCornerToNorm(corners, pageWidthPt, pageHeightPt) {
    if (!(pageWidthPt > 0) || !(pageHeightPt > 0)) {
        throw new GroundTruthExportError(`pdfUserSpaceCornerToNorm: non-positive page dims (${pageWidthPt}x${pageHeightPt})`);
    }
    const [x0, y0, x1, y1] = corners;
    const lowerX = Math.min(x0, x1);
    const upperX = Math.max(x0, x1);
    const lowerY = Math.min(y0, y1);
    const upperY = Math.max(y0, y1);
    return {
        x: lowerX / pageWidthPt,
        // Origin flip: top edge (top-left space) = pageHeight - upperY (PDF space).
        y: (pageHeightPt - upperY) / pageHeightPt,
        w: (upperX - lowerX) / pageWidthPt,
        h: (upperY - lowerY) / pageHeightPt,
    };
}
/**
 * Hand-authored side, xywh form: the IR `Block.bbox` is a `{x,y,w,h}` box in PDF
 * user-space with a BOTTOM-left origin (the same space as `EditorIRBBox` —
 * `set-bbox`/`create-element` store `canvasBboxToPdf` output, and the wire IR
 * arrives from the server in PDF user-space, never transformed on load). `y` is the
 * box's BOTTOM edge measured from the page bottom. Reconciling to the canonical
 * top-left-origin `BboxNorm` therefore requires the ORIGIN FLIP: the box's top edge
 * in top-left space is `pageHeight - (y + h)`. This is the hand-authored analogue of
 * `pdfUserSpaceCornerToNorm`; getting the flip wrong is the silent-noise hazard the
 * regression pin (`ground-truth-doc.jsdom.test.ts`) guards against.
 */
export function pdfUserSpaceXywhToNorm(bbox, pageWidthPt, pageHeightPt) {
    if (!(pageWidthPt > 0) || !(pageHeightPt > 0)) {
        throw new GroundTruthExportError(`pdfUserSpaceXywhToNorm: non-positive page dims (${pageWidthPt}x${pageHeightPt})`);
    }
    return {
        x: bbox.x / pageWidthPt,
        // Origin flip: top edge (top-left space) = pageHeight - (y + h) (PDF space,
        // bottom-left origin where `y` is the box's BOTTOM edge).
        y: (pageHeightPt - (bbox.y + bbox.h)) / pageHeightPt,
        w: bbox.w / pageWidthPt,
        h: bbox.h / pageHeightPt,
    };
}
/**
 * Intersection-over-union of two normalized boxes (0..1). The G-6 alignment metric
 * once both sides are in the canonical scoring space. Returns 0 for disjoint boxes.
 */
export function bboxIoU(a, b) {
    const ax2 = a.x + a.w;
    const ay2 = a.y + a.h;
    const bx2 = b.x + b.w;
    const by2 = b.y + b.h;
    const ix = Math.max(0, Math.min(ax2, bx2) - Math.max(a.x, b.x));
    const iy = Math.max(0, Math.min(ay2, by2) - Math.max(a.y, b.y));
    const inter = ix * iy;
    if (inter <= 0)
        return 0;
    const union = a.w * a.h + b.w * b.h - inter;
    if (union <= 0)
        return 0;
    return inter / union;
}
/** PDF/UA role for an IR block (mirrors serializeIRToStructList's role mapping). */
function blockRole(b) {
    switch (b.kind) {
        case "Heading": return `H${b.level}`;
        case "Paragraph": return "P";
        case "Figure": return "Figure";
        case "Formula": return "Formula";
        case "Caption": return "Caption";
        case "Note": return "Note";
        case "Span": return "Span";
        case "Artifact": return "Artifact";
        case "Table": return "Table";
        case "List": return "L";
        default: {
            // CRP-5 (design §H.2): the switch is exhaustive over the Block union,
            // so this arm is unreachable for well-typed IR. A malformed/unknown
            // `kind` at runtime previously fell through to `undefined` and
            // propagated silently (A.14). Return a safe sentinel role ("P", a
            // universally-valid PDF/UA role — the same fallback buildBlockForRole
            // uses) and surface the drift via log.warn.
            log.warn("[editor:ground-truth-doc] blockRole: unknown block kind — safe 'P' fallback (CRP-5)", { kind: b.kind });
            return "P";
        }
    }
}
function inlineText(inlines) {
    if (!inlines)
        return "";
    return inlines.map((il) => {
        if (il.kind === "Text")
            return il.value;
        if (il.kind === "Run")
            return inlineText(il.content);
        return "";
    }).join("");
}
/** Text payload for an element, mirroring the serializer's per-kind text. */
function blockText(b) {
    switch (b.kind) {
        case "Heading":
        case "Paragraph":
        case "Caption":
        case "Note":
        case "Span":
            return inlineText(b.content);
        case "Formula": return b.latex;
        case "Artifact": return b.raw;
        case "Figure": return null;
        case "Table": return null;
        case "List": return null;
        default: {
            // CRP-5 (design §H.2): unreachable for well-typed IR (exhaustive over
            // the Block union). A malformed/unknown `kind` previously returned
            // `undefined` and propagated silently — return the safe `null` sentinel
            // (the same "no text payload" value Figure/Table/List carry) + log.warn.
            log.warn("[editor:ground-truth-doc] blockText: unknown block kind — safe null fallback (CRP-5)", { kind: b.kind });
            return null;
        }
    }
}
/**
 * Human-readable, actionable message for a geometry-less-blocks export refusal.
 * Names up to the first 10 blocks (role + structId) with a `+N more` tail so a
 * pathological document doesn't produce a wall of text; the full set is always on
 * the error's `unplacedBlocks` field.
 */
function formatUnplacedMessage(unplaced) {
    const n = unplaced.length;
    const preview = unplaced.slice(0, 10).map((u) => `${u.role} ${u.structId}`).join(", ");
    const more = n > 10 ? `, +${n - 10} more` : "";
    return `exportGroundTruth: ${n} element${n === 1 ? "" : "s"} lack geometry and must be `
        + `placed (draw a box in the editor) before ground-truth export — ${preview}${more}. `
        + `Ground truth requires a bbox on every element (GT-INV-1b); these blocks have no `
        + `derivable geometry, so they cannot be scored until placed.`;
}
/**
 * Serialize a hand-authored IR `Document` to a `GroundTruthDoc` (Ph1b R1: BOTH the
 * structure round-trip AND the geometry leg). Flat `elements[]` in document reading
 * order; List item content is nested via `parentGtId`.
 *
 * FAIL-LOUD (GT-INV-1b / A.14): every emitted element must have a real bbox and a
 * resolvable page canvas. A ground-truth element with no box is unscoreable under
 * G-6, so `exportGroundTruth` refuses to serialize rather than emit a box the
 * scorer would later mis-handle.
 *
 * GEOMETRY-LESS BLOCKS — loud, not silent (Phase 6). Some tagged-doc IR blocks
 * arrive with `bbox === null`. This is NOT re-derivable on the client: the backend
 * `PdfStructTree.DeriveBBox` already exhausts every source of geometry —
 * `/A[/Layout]/BBox`, an MCID glyph-bbox union (`TryDeriveBBoxFromMcids`, the same
 * MCID layout the overlay uses), and a recursive child-bbox union — so a block that
 * still has no bbox has genuinely NO derivable geometry (empty / whitespace / OBJR
 * marked content, an MCID-less container, or an MCID whose page cannot be resolved).
 * The wire IR carries no struct→MCID linkage on the client (McidGeometry has no
 * `parentStructId`; `interop.buildIrByIdMap` leaves per-struct `mcids` null on the
 * backend load path), so the client cannot improve on the backend's result. Instead
 * of dying on the FIRST such block, we collect them ALL and fail loud with the
 * complete set (message + `unplacedBlocks`), so the author places every one and
 * re-exports once — never a silent skip, never a synthesized box (that would poison
 * the G-6 IoU oracle), never a weakened GT-INV-1b. A geometry-less container is
 * reported and its subtree skipped; its children re-surface after it is placed.
 *
 * Table cells + list-item Lbl/LBody are OUT of the geometry scope for now (they
 * carry no bbox in the IR — §5.3 flatten, a documented later-phase fidelity gap);
 * only bbox-bearing Blocks are emitted.
 */
export function exportGroundTruth(ir, provenance, pageCanvas) {
    const elements = [];
    const unplaced = [];
    let nextId = 0;
    let readingOrder = 0;
    const emit = (b, parentGtId) => {
        if (!b.bbox) {
            // Geometry-less: record it (loud surface below) and skip the subtree.
            // See the function docstring — this is genuinely un-derivable, not a
            // client-side re-derivation gap.
            unplaced.push({ structId: b.structId, role: blockRole(b) });
            return;
        }
        const canvas = pageCanvas(b.page);
        if (!canvas || !(canvas.w > 0) || !(canvas.h > 0)) {
            throw new GroundTruthExportError(`exportGroundTruth: no page-canvas dims for page ${b.page} `
                + `(block ${b.structId}) — cannot normalize geometry.`);
        }
        const gtId = `gt-${nextId++}`;
        readingOrder += 1;
        const isFigure = b.kind === "Figure";
        elements.push({
            gtId,
            role: blockRole(b),
            page: b.page,
            readingOrderIndex: readingOrder,
            // Block.bbox is PDF user-space (bottom-left origin); reconcile to the
            // canonical top-left BboxNorm WITH the origin flip. `canvas.{w,h}` are the
            // page MediaBox dims in PDF points (the PageCanvasLookup contract below).
            bbox: pdfUserSpaceXywhToNorm(b.bbox, canvas.w, canvas.h),
            geometryConfidence: "tight",
            text: blockText(b),
            alt: isFigure ? b.alt : null,
            actualText: isFigure ? b.actualText : null,
            decorative: isFigure ? b.decorative : false,
            parentGtId,
        });
        // List item content nests under the List element (parentGtId). List-item
        // content is OUT of the current geometry scope (see docstring §5.3): emit
        // ONLY bbox-bearing item children and SKIP bbox-less ones — a bbox-less
        // item block is a documented fidelity gap (the item-geometry fork:
        // text-fold vs per-item boxes), NOT an unplaced-refusal. The GT-INV-1b
        // fail-loud is reserved for TOP-LEVEL authored blocks the user must place;
        // an MVP List coerced from a single block (editor-list-rca) carries a
        // bbox on the List itself and a bbox-less inner Paragraph, and must still
        // export as an "L" element rather than refusing.
        if (b.kind === "List") {
            for (const item of b.items) {
                for (const child of item.content) {
                    if (child.bbox)
                        emit(child, gtId);
                }
            }
        }
    };
    for (const b of ir.blocks)
        emit(b, null);
    // GT-INV-1b: refuse to export while any element lacks geometry — but name the
    // WHOLE set (not just the first) so the author places them in one pass.
    if (unplaced.length > 0) {
        throw new GroundTruthExportError(formatUnplacedMessage(unplaced), unplaced);
    }
    return {
        schemaVersion: GROUND_TRUTH_SCHEMA_VERSION,
        format: provenance.format ?? "pdf",
        sourceDocSha256: provenance.sourceDocSha256,
        sourceDocName: provenance.sourceDocName,
        authoredBy: provenance.authoredBy,
        authoredAtUtc: provenance.authoredAtUtc ?? new Date().toISOString(),
        editorVersion: provenance.editorVersion,
        pageCount: provenance.pageCount ?? (ir.pages ? ir.pages.length : null),
        title: ir.title,
        language: ir.language,
        elements,
    };
}
